const compareNumbers = (a, b) => a - b;
const sum = (a, b) => a + b;

const getPrice = (option, paymentOptions) => option?.price != null ? option.price : paymentOptions.defaultDivisionPrice;

export const getOptionPrice = (optionId, paymentOptions) =>
    getPrice((paymentOptions.divisions || []).find(d => d.id === optionId), paymentOptions);

const getExtraDivisionPrice = (paymentOptions, optionPrice) => paymentOptions.extraDivisionPrice != null ? paymentOptions.extraDivisionPrice : optionPrice;

export const getOptionsPriceRange = (paymentOptions, totalDivisions) => {
    const paymentOptionDivisions = paymentOptions.divisions || [];
    const divisionPrices = (paymentOptionDivisions).reduce((prices, division) => {
        const optionPrice = getPrice(division, paymentOptions);
        if (prices.indexOf(optionPrice) === -1) prices.push(optionPrice);
        return prices;
    }, paymentOptions.defaultDivisionPrice != null && paymentOptionDivisions.length < totalDivisions ? [paymentOptions.defaultDivisionPrice] : []);

    return divisionPrices.length > 1 ? [Math.min(...divisionPrices), Math.max(...divisionPrices)] : divisionPrices;
};

const calculateOptionsSubtotal = (registrationIds, paymentOptions, includeExtraDivisionDiscount = true) => {
    const [biggestPriceOptionId] = registrationIds.reduce(([mostExpensiveId, price], id) => {
        const optionPrice = getOptionPrice(id, paymentOptions);
        if (optionPrice > (price || -1)) return [id, optionPrice];
        return [mostExpensiveId, price];
    }, []);

    return registrationIds
        .map(id => {
            const optionPrice = getOptionPrice(id, paymentOptions);
            if (!includeExtraDivisionDiscount) return optionPrice;

            return id === biggestPriceOptionId ?
                optionPrice :
                Math.min(getExtraDivisionPrice(paymentOptions, optionPrice), optionPrice);
        })
        .reduce(sum, 0);
};

const getClosestLow = (array, n) => array.reduce((prev, curr) => Math.abs(curr - n) < Math.abs(prev - n) ? curr : prev, 0);

const calculateOptionsTotal = (athletes, familyTotal, subtotals) => {
    const familyTotalKeys = familyTotal && Object.keys(familyTotal).map(i => parseInt(i));
    const familyDiscountTotal = familyTotal && (familyTotal[athletes.toString()] ||
        (athletes > Math.min(...familyTotalKeys) && familyTotal[getClosestLow(familyTotalKeys.sort(compareNumbers), athletes).toString()]));

    const optionsTotal = subtotals.reduce(sum, 0);

    return {
        total: Math.min(...[familyDiscountTotal, optionsTotal].filter(t => t != null && t !== false)),
        subtotal: optionsTotal
    };
};

export const calculateTotal = (registrations, paymentOptions) => {
    const subtotals = registrations.map(registration => {
        const paidRegistrationIdMap = (registration.registrations_attributes || []).filter(r => r?.id).map(r => r.option_id);
        const unpaidRegistrationIdMap = (registration.registrations_attributes || []).filter(r => r && !r.id).map(r => r.option_id);
        const registrationIdMap = paidRegistrationIdMap.concat(unpaidRegistrationIdMap);

        const extrasUuids = registration.extras?.filter(e => e).map(e => e.uuid);
        const extrasTotal = paymentOptions.extras
            ?.filter(e => extrasUuids?.indexOf(e.uuid) > -1)
            ?.map(e => e.price)
            ?.reduce(sum, 0);

        return {
            allOptions: calculateOptionsSubtotal(registrationIdMap, paymentOptions),
            paidOptions: calculateOptionsSubtotal(paidRegistrationIdMap, paymentOptions),
            unpaidOptionsBeforeDiscount: calculateOptionsSubtotal(unpaidRegistrationIdMap, paymentOptions, false),
            extras: extrasTotal || 0
        };
    });

    const allOptionsTotal = calculateOptionsTotal(
        subtotals.filter(sub => sub.allOptions || sub.paidOptions).length,
        paymentOptions.familyTotal,
        subtotals.map(sub => sub.allOptions)
    );

    const paidOptionsTotal = calculateOptionsTotal(
        subtotals.filter(sub => sub.paidOptions).length,
        paymentOptions.familyTotal,
        subtotals.map(sub => sub.paidOptions)
    );

    const extrasTotal = subtotals.map(sub => sub.extras).reduce(sum, 0);
    const subtotal = subtotals.map(sub => sub.unpaidOptionsBeforeDiscount).reduce(sum, 0);

    return {
        total: Math.max(allOptionsTotal.total - paidOptionsTotal.total + extrasTotal, 0),
        subtotalWithoutFamilyDiscount: allOptionsTotal.subtotal - paidOptionsTotal.total + extrasTotal,
        subtotal: subtotal + extrasTotal,
        extras: extrasTotal
    };
};

export const getOptionDiscountPrice = (athleteRegistrations = [], optionId, paymentOptions) => {
    const optionPrice = getOptionPrice(optionId, paymentOptions);
    if (optionPrice == null || paymentOptions.extraDivisionPrice == null) return optionPrice;

    const extraOptionPrice = getExtraDivisionPrice(paymentOptions, optionPrice),
        selectedOptionsWithPrice = athleteRegistrations.filter(r => getOptionPrice(r.option_id, paymentOptions) > 0);

    if (optionPrice !== extraOptionPrice && selectedOptionsWithPrice.length > 0) {
        const [mostExpensiveSelected] = athleteRegistrations.reduce(([mostExpensive, price], option) => {
            const optionPrice = getOptionPrice(option.option_id, paymentOptions);
            if (optionPrice > (price || -1)) return [option, optionPrice];
            return [mostExpensive, price];
        }, []);
        const paidRegistrationPrices = athleteRegistrations.filter(r => r.id).map(r => getOptionPrice(r.option_id, paymentOptions)).sort(compareNumbers);
        const mostExpensivePaid = paidRegistrationPrices[paidRegistrationPrices.length - 1] || extraOptionPrice;

        if (optionId === mostExpensiveSelected?.option_id) {
            return optionPrice - mostExpensivePaid + Math.min(mostExpensivePaid, extraOptionPrice);
        }
        return Math.min(extraOptionPrice, optionPrice);
    }
    return optionPrice;
};
